/**
 * Hook to communicate with the ADP main processs via ADP Electron or Chrome extension bridge
 *
 */
import { Dispatch } from "react";
import { IADPState, NetworkConnectionState, UpdateStates, actions, deviceType } from "../state";
import { IADPActions } from "../state/Actions";
import { ExtensionMessage } from "../../Types";
import { v4 as uuidv4 } from "uuid";
import { getPathName, isPageReloaded, randomstring } from "../util/util";
import { IpcMessageType, SessionDetails } from "../types/BridgeTypes";
import { useResolveServiceStatus } from "./useNetworkService";
import { ValidationSummary} from "@npd-ng/adp-types";
type client = {
  clientCode:string,
  clientVersion:string,
  clientBuildDate:string,
  clientURL:string
}
export type ExtensionResponse = {
  type: string,
  data?:any
}
declare global {
  interface Window {
    adpElectronBridge?: {
      greet: (arg0: client) => Promise<SessionDetails>;
      isUpdateAvailable: () => Promise<IpcMessageType>;
      postChromeAppState:(state:IADPState)=>void;
      lockdownApp:()=>void,
      listenOnNetworkStatusChanges:()=>void;
      sendSetupCode: (setupCode:string) => void;
      sendDeliveryMode: (deliveryMode:string) => void;
      validateVitals: () => Promise<ValidationSummary>
    };
  }
}
export const isElectronBridgeAvailable = () => {
  return window["adpElectronBridge"];
};
export const postMessageToAdpBridge = (message: ExtensionMessage) => {
  //if(window.adp)
  window.postMessage(message);
};
export function useIPCBridge(
  dispatch: Dispatch<IADPActions>,
  state?: IADPState
) {
  const win: Window = window;
   const {isDeviceConnectedToNAEPRouter} = useResolveServiceStatus(dispatch);
   const getDeviceType= ():deviceType =>{
    //const regexToMatch = ["^[A,B]{1}\\d{5}$"]
  // console.info("[Util] the asset id is: ",assetTag,regexToMatch)
    let deviceType = "SBE" as deviceType;
    /* regexToMatch?.forEach((pattern)=> {
      const regex = RegExp(pattern);
      if(assetTag){
        console.info("[Util] username and length is: ",regexToMatch,assetTag,regex.test(assetTag))
        if(regex.test(assetTag)) {
            console.log("[Util] The asset ID matches the list of acceptable asset IDs",name,assetTag);
            deviceType = "NPE";
        }
      }
    }); */
    //for SBE POC validation chromebooks have the same asset tag pattern as NPE. Adding query param to distinguish between SBE and NPE chromebooks managed by SDC
    if(getPathName().includes("npe/index.html")){
       deviceType = "NPE";
       console.log("Device is managed by naep", getPathName());
    } 
    console.info("[Util] Changing device type to ", deviceType);

    return deviceType
  }
  /**
   * send greeting to ADP electron main process or ADP chrome extension.
   * If none is available, application is runnng in the browser without any backend support;
   * >>> Communication with Electron is established via preload.js which is part of the adp electron app and is injected
   * on the app startup. All methods are available at Window scope
   * >>> Communcatioon with Chrome Extension is established via Events that are emitted to the Window's context
   */
  function sendGreeting() {
    //send greeting to Electron and obtain session and version details from the main process, if electron is not available read local manifest file
    const hardwareInfo=()=>{
      const promise =  new Promise<{hardwareInfo:any | string,systemInfo: any}>((resolve) => {
        const eventTimeoutId = setTimeout(() => {
          resolve({hardwareInfo:"did not resolve",systemInfo: "did not resolve"});
        }, 5000);
          const handler =(event: any)=>{
            clearTimeout(eventTimeoutId);
            resolve(event.detail?.data);
              window.removeEventListener('HardwareInfoEvent',handler);
          }
            window.addEventListener('HardwareInfoEvent', handler);
        });
      
        return promise;
    }
    if (isElectronBridgeAvailable()) {
      console.log("[ADPClient][IPCBridge] App is running in Electron, send greeting to main process");
      win["adpElectronBridge"]?.greet({
        clientCode:state?.appState.appCode as string,
        clientVersion:state?.appState.appVersion as string,
        clientBuildDate: state?.appState.appBuildDate as string,
        clientURL:window.location.href
      }).then(async (details: any) => {
        const sessionDetails: SessionDetails = {
          ...details,
          deviceId:details.deviceId.replace(/[^a-z0-9]/gi, ''),
          deviceType:details.deviceType,//device type will be set by electron, no need to modify
          networkConnectionState:details.networkConnectionState?.connectionType ? details.networkConnectionState?.connectionType:details.networkConnectionState 
          && details.networkConnectionState.length > 0 ? details.networkConnectionState[0]?.connectionType :
           window.navigator.onLine ? "online" : "offline" as NetworkConnectionState,
          networkConnectionDetails: details.networkConnectionState ,
          isPageReloaded: isPageReloaded(),
          runtimePlatform: {
            platform: "Electron",
            version: details.version,
            userAgent: navigator.userAgent,
          },
        };
        const nscEvent = new CustomEvent('network-state-changed-init', {
          detail: sessionDetails.networkConnectionState,
        });
        window.dispatchEvent(nscEvent);
        try {
          console.log("[ADPClient][IPCBridge] send message to extenstion for hardware info")
          hardwareInfo().then((info)=>{
            console.log("hardwareInfo from extenstion",info)
          })
        }
        catch(e){
          console.warn("error getting hardwareInfo from extenstion",e);
        }
        //We can't store the device validation log at this point as logHeader is invalid, at this point state is missing critical values. 
       /*  if(state){
          const {logHeader} = RequestService.constructDeviceValidationDataTransferPayload(state);
          indexDBStorage.storeValidationLogs({logHeader:logHeader,deviceValidationChecks:sessionDetails.deviceValidationChecks},sessionDetails.appLaunchId);
        }  */
        //check date time
        dispatch(actions.createSetSessionDetailsAction(sessionDetails));
        //subscribe to network events
        win["adpElectronBridge"]?.listenOnNetworkStatusChanges();
        //send greeting to the Chrome Extension loaded with Electron 
        console.log("[ADPClient][IPCBridge] send greeting to ADP extension...");
        postMessageToAdpBridge({
          type: "Greeting",
          data: { client: "ADP", appLaunchId: sessionDetails.appLaunchId },
        });
      });
    } else {
      console.log("[ADPClient][IPCBridge] App is running in Browser, send greeting to chrome extension"
      );
      //send greeting to chrome extension to check if Chrome extension is available
      const appLaunchId = state?.appState.appLaunchId ||  uuidv4();
      const deviceId = `CB${randomstring(4).toUpperCase()}`
      const sessionDetails: SessionDetails = {
        appLaunchId: appLaunchId,
        deviceId: deviceId, //TODO
        deviceModel: navigator.userAgent, //TODO
        deviceType: getDeviceType(),
        networkConnectionState:window.navigator.onLine ? "online" : "offline" as NetworkConnectionState,
        isPageReloaded: isPageReloaded(),
        runtimePlatform: {
          platform: "Browser",
          version: "",
          userAgent: navigator.userAgent,
        },
      };
      //TODO - find a better way to check if extension is installed, e.g inject extension version
      const timeoutId = setTimeout(() => {
        console.log("[ADPClient][IPCBridge] extension is not available");
        console.warn('[ADPClient][IPCBridge] app is running with limited functionalites, consider adding ADP Chrome extension for full experience')
        dispatch(actions.createSetSessionDetailsAction(sessionDetails));
      }, 5000);
      console.log("[ADPClient][IPCBridge] send greeting to ADP extension...");
      postMessageToAdpBridge({
        type: "Greeting",
        data: { client: "ADP", appLaunchId: appLaunchId },
      });
      //listen for event from ChrEx
      const handler = (event: any) => {
        console.log(
          "[ADPClient][IPCBridge] Chrome Extension Version:",
          event?.detail?.data?.chromeExtensionVersion
        );
        dispatch(actions.createSetUpdateComponentVersionsAction({name: "ADP-EXTENSION", version: event?.detail?.data?.chromeExtensionVersion }));
        clearTimeout(timeoutId);
          window.removeEventListener("GreetingEvent", handler);
          const platformInfo=()=>{
           
            const promise =  new Promise<{os: string,arch: string,nacl_arch:string}>((resolve) => {
                const handler =(event: any)=>{
                  resolve(event.detail?.data);
                  window.removeEventListener('PlatformInfoEvent',handler);
                }
                  window.addEventListener('PlatformInfoEvent', handler);
              })
              return promise;
            };
          const  deviceDetails=()=>{
              const promise =  new Promise<string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve("DeviceDetailsEvent did not resolve");
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                    window.removeEventListener('DeviceDetailsEvent',handler);
                  }
                    window.addEventListener('DeviceDetailsEvent', handler);
                })
                return promise;
            }
           const networkDetails=()=>{
              const promise =  new Promise<{macAddress: string, ipv4:string,ipv6:string} | string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve("NetworkDetailsEvent did not resolve");
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                      window.removeEventListener('NetworkDetailsEvent',handler);
                  }
                    window.addEventListener('NetworkDetailsEvent', handler);
                });
              
                return promise;
            }

           
            Promise.all([platformInfo(),deviceDetails(),networkDetails(),hardwareInfo()]).then((values)=>{
              console.log(values);
              const deviceId = values[1]?.indexOf("is not available") > -1 ? `CB${randomstring(4).toUpperCase()}` : values[1].replace(/[^a-z0-9]/gi, '');
              const address =  typeof values[2] === "string" ? values[2] : values[2]?.ipv4
              const network = typeof values[2] === "string" ?  window.navigator.onLine ? "online" : "offline" as NetworkConnectionState : 
               isDeviceConnectedToNAEPRouter(address) ? "router" :  window.navigator.onLine ? "online" : "offline" as NetworkConnectionState
               const deviceModel = typeof values[3] === "string" || !values[3]?.hardwareInfo ? navigator.userAgent : values[3]?.hardwareInfo;
               const systemInfo = values[3]?.systemInfo;
               const sessionDetails: SessionDetails = {
                deviceId: deviceId,
                deviceModel:deviceModel,
                deviceType: getDeviceType(),
                appLaunchId: appLaunchId,
                isPageReloaded: isPageReloaded(),
                networkConnectionState:network,
                networkConnectionDetails: {connectionType:network,address: address},
                systemInfo: systemInfo,
                runtimePlatform: {
                  platform: "ChromeWithExtension",
                  version: event?.detail?.data?.chromeExtensionVersion,
                  userAgent: navigator.userAgent,
                },
              }; 
              dispatch(actions.createSetSessionDetailsAction(sessionDetails));
              const nscEvent = new CustomEvent('network-state-changed', {
                detail: network,
              });
              window.dispatchEvent(nscEvent);
            });
           
           postMessageToAdpBridge({type:"NetworkDetails"}); 
           postMessageToAdpBridge({type:"DeviceDetails"}); 
           postMessageToAdpBridge({type:"PlatformInfo"}); 
           postMessageToAdpBridge({type:"HardwareInfo"});   
          }
             
      window.addEventListener("GreetingEvent", handler);
    }
  }
  function getVitals() {
    if (isElectronBridgeAvailable()) {
      console.log("[ADPClient][IPCBridge] App is running in Electron, send vitals check.");
      win["adpElectronBridge"]?.validateVitals().then((summary:ValidationSummary)=>{

      })
    }
    else{
      console.log("[ADPClient][IPCBridge] App is not running in Electron, skipping vitals check.");
    }
  }
  function checkForUpdates() {
    const onUpdateEvents = (e:any) => {
      console.log("[ADPClient][IPCBridge][onUpdateEvents]");
      console.table(e.detail);
      if(e.detail?.type === "update-fast-forward"){
        dispatch(actions.createSetViewAction("ModeSelectionsView"));
      }
      else if(e.detail?.type === "update-is-in-progress"){
        dispatch(actions.createSetViewAction("UpdaterView"));
      }
      else {
        dispatch(actions.createUpdateProgressAction(e.detail));
      }
    };
    window.addEventListener("update-progress",onUpdateEvents);
    win["adpElectronBridge"]
      ?.isUpdateAvailable()
      .then((data: IpcMessageType) => {
        console.log("[ADPClient][IPCBridge]");
        console.table(data);
        const updateType:UpdateStates = data.type;
        window.removeEventListener("update-progress",onUpdateEvents);
        if (updateType === "update-not-available") {
          dispatch(actions.createUpdateProgressAction(data));
          setTimeout(()=>{
            dispatch(actions.createSetViewAction("ModeSelectionsView"));
         },5000);
        } else if (updateType === "update-error") {
          dispatch(actions.createErrorAction("UpdateError"));
        }
        else if (updateType === "update-complete") {
          setTimeout(()=>{
            dispatch(actions.createUpdateProgressAction(data));
         },1000);
        }
        else if(updateType === "update-fast-forward"){
          dispatch(actions.createSetViewAction("ModeSelectionsView"));
        }
        else if(updateType === "update-is-in-progress"){
          dispatch(actions.createSetViewAction("UpdaterView"));
        }
      })
      .catch((e) => {
        console.error("[ADPClient][IPCBridge]", e);
        dispatch(actions.createErrorAction("UpdateError"));
        window.removeEventListener("update-progress",onUpdateEvents);
      });
  }
  function saveADPChromeAppState(state:IADPState){
    if (isElectronBridgeAvailable()) {
      win["adpElectronBridge"]?.postChromeAppState(state)
    }
    else {
      postMessageToAdpBridge({
        type: "PostChromeAppState"
      });
      
    }
  };
  function lockDownElectronApp(){
    win["adpElectronBridge"]?.lockdownApp();
  }
  function sendSetupCode (setupCode:string){
    win["adpElectronBridge"]?.sendSetupCode(setupCode);
  }
  function sendDeliveryMode (deliveryMode:string){
    win["adpElectronBridge"]?.sendDeliveryMode(deliveryMode);
  }
  return { sendGreeting, checkForUpdates,saveADPChromeAppState,startAssessment: lockDownElectronApp,sendSetupCode,sendDeliveryMode,getVitals};
};

